import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Download, Trash2, Search, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

const STATUS = {
  inviata: { label: "Inviata", cls: "bg-blue-100 text-blue-800" },
  test_in_corso: { label: "Test in corso", cls: "bg-amber-100 text-amber-800" },
  completata: { label: "Completata", cls: "bg-green-100 text-green-800" },
  non_completata: { label: "Non completata", cls: "bg-red-100 text-red-800" },
  annullata: { label: "Annullata", cls: "bg-red-100 text-red-800" },
};

export default function CandidatesTab() {
  const [apps, setApps] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseFilter, setCourseFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleting, setDeleting] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("data");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/courses").then(({ data }) => setCourses(data.filter((c) => c.active))).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (courseFilter !== "all") params.set("course_id", courseFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    api.get(`/admin/applications?${params.toString()}`)
      .then(({ data }) => { setApps(data); setLoading(false); })
      .catch(() => setLoading(false));
    api.get("/admin/applications/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [courseFilter, statusFilter]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { setPage(1); }, [search, courseFilter, statusFilter, sortKey, sortDir]);

  // Ricerca e ordinamento valgono sull'intero elenco caricato, non sulla pagina visibile
  const filteredApps = apps.filter((a) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (a.anagrafica?.nome || "").toLowerCase().includes(q)
        || (a.anagrafica?.cognome || "").toLowerCase().includes(q);
  });
  const COMPARE = {
    nome: (a, b) => (a.anagrafica?.nome || "").localeCompare(b.anagrafica?.nome || "", "it", { sensitivity: "base" }),
    cognome: (a, b) => (a.anagrafica?.cognome || "").localeCompare(b.anagrafica?.cognome || "", "it", { sensitivity: "base" }),
    stato: (a, b) => (STATUS[a.status]?.label || a.status || "").localeCompare(STATUS[b.status]?.label || b.status || "", "it"),
    data: (a, b) => String(a.created_at).localeCompare(String(b.created_at)),
  };
  const sortedApps = [...filteredApps].sort((a, b) => (sortDir === "asc" ? 1 : -1) * COMPARE[sortKey](a, b));
  const PAGE_SIZE = 50;
  const totalPages = Math.max(1, Math.ceil(sortedApps.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = sortedApps.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "data" ? "desc" : "asc"); }
  };
  const sortHead = (id, label) => (
    <TableHead key={id}>
      <button type="button" data-testid={`sort-header-${id}`} onClick={() => toggleSort(id)}
        className="inline-flex items-center gap-1 transition-colors hover:text-[#003366]">
        {label}
        {sortKey === id
          ? (sortDir === "asc" ? <ArrowUp data-testid={`sort-indicator-${id}`} className="h-3.5 w-3.5 text-[#003366]" /> : <ArrowDown data-testid={`sort-indicator-${id}`} className="h-3.5 w-3.5 text-[#003366]" />)
          : <ArrowUpDown className="h-3.5 w-3.5 text-slate-300" />}
      </button>
    </TableHead>
  );

  const doDelete = async () => {
    try {
      await api.delete(`/admin/applications/${deleting.id}`);
      toast.success("Candidatura eliminata");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDeleteAll = async () => {
    try {
      const { data } = await api.delete("/admin/applications");
      toast.success(`${data.deleted} candidature eliminate dal database`);
      setDeletingAll(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-3" data-testid="admin-candidates-filters">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="candidates-search" className="w-64 pl-9" placeholder="Cerca per nome o cognome…"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={courseFilter} onValueChange={setCourseFilter}>
            <SelectTrigger data-testid="filter-course" className="w-64"><SelectValue placeholder="Tutti i corsi" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i corsi</SelectItem>
              {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status" className="w-56"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="admin-export-csv" variant="outline" asChild>
            <a href={`${API}/admin/export/candidature.csv`} download>
              <Download className="h-4 w-4 mr-2" /> Esporta CSV
            </a>
          </Button>
          <Button data-testid="admin-delete-all" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
            disabled={apps.length === 0} onClick={() => setDeletingAll(true)}>
            <Trash2 className="h-4 w-4 mr-2" /> Elimina tutte le candidature
          </Button>
        </div>
      </div>

      <Card className="institutional-card">
        <CardContent className="p-0">
          {stats && (
            <div className="flex flex-wrap items-center gap-3 px-4 pt-4" data-testid="candidates-counter">
              <Badge data-testid="candidates-total-count" className="bg-[#003366] text-white text-sm px-3 py-1.5">
                {stats.total} candidature totali
              </Badge>
              {courseFilter !== "all" && (
                <Badge data-testid="candidates-filtered-count" variant="secondary" className="text-sm px-3 py-1.5">
                  {stats.by_course?.[courseFilter] ?? 0} per il corso selezionato
                </Badge>
              )}
            </div>
          )}
          <div className="overflow-x-auto">
          <Table data-testid="admin-candidates-table">
            <TableHeader>
              <TableRow>
                {sortHead("nome", "Nome")}
                {sortHead("cognome", "Cognome")}
                <TableHead>Corso (1ª preferenza)</TableHead>
                {sortHead("stato", "Stato")}
                <TableHead>Sezione A</TableHead>
                {sortHead("data", "Data di candidatura")}
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && pageItems.map((a) => {
                const s = STATUS[a.status] || STATUS.inviata;
                const sess = a.test_session;
                return (
                  <TableRow key={a.id} data-testid={`admin-candidate-row-${a.id}`} className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/admin/candidature/${a.id}`)}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{a.anagrafica?.nome}</div>
                      <div className="text-xs text-slate-500 font-mono2">{a.anagrafica?.codice_fiscale}</div>
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{a.anagrafica?.cognome}</TableCell>
                    <TableCell className="text-sm text-slate-700">{a.course_name}</TableCell>
                    <TableCell><Badge className={s.cls}>{s.label}</Badge></TableCell>
                    <TableCell>
                      {sess?.section_a_total != null ? (
                        <span className="font-mono2 font-semibold text-[#003366]">{sess.section_a_total}/{sess.section_a_max ?? "—"}</span>
                      ) : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{new Date(a.created_at).toLocaleDateString("it-IT")}</TableCell>
                    <TableCell>
                      <Button data-testid={`delete-application-${a.id}`} variant="ghost" size="sm"
                        onClick={(e) => { e.stopPropagation(); setDeleting(a); }}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && sortedApps.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500 py-8">Nessuna candidatura trovata con questi filtri.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          </div>
          {!loading && sortedApps.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3" data-testid="candidates-pagination">
              <p className="text-xs text-slate-500" data-testid="candidates-page-info">
                {sortedApps.length} candidature in totale — pagina {safePage} di {totalPages}
              </p>
              <div className="flex gap-2">
                <Button data-testid="candidates-prev-page" variant="outline" size="sm" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>Precedente</Button>
                <Button data-testid="candidates-next-page" variant="outline" size="sm" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>Successiva</Button>
              </div>
            </div>
          )}
          {loading && <p className="text-sm text-slate-500 text-center py-8">Caricamento candidature...</p>}
        </CardContent>
      </Card>

      <AlertDialog open={deletingAll} onOpenChange={(v) => !v && setDeletingAll(false)}>
        <AlertDialogContent data-testid="delete-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina tutte le candidature</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare tutte le candidature? L'operazione è irreversibile.
              Verranno cancellati solo i dati dal database dell'app: le cartelle e i documenti su
              Google Drive saranno conservati e non verranno toccati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-all-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-all-confirm" className="bg-red-600 hover:bg-red-700" onClick={doDeleteAll}>
              Elimina tutto definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(deleting)} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-application-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina candidatura</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questa candidatura? L'operazione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-application-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-application-confirm" className="bg-red-600 hover:bg-red-700" onClick={doDelete}>
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
